import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { isUserEurostarAgent } from 'utils/booking';
import { isTravelAgent } from 'utils/user';
import { Page } from 'components/shared/Layout';

import { useDefaultTab } from './BookingSearch.utils';
import { locationShape } from 'constants/propTypes';
import Tabs from 'components/ui/Tabs';
import SearchByOnHoldPaymentMonth from './SearchByOnHoldPaymentMonth';
import SearchByOtherDetailsForm from './SearchByOtherDetailsForm';
import SearchByPnrForm from './SearchByPnrForm';
import SearchByMinors from './SearchByMinors';
import SearchPerTrainForm from './SearchPerTrain';
import SearchBySpecialAssistance from './SearchBySpecialAssistance';
import SearchByLuggage from './SearchByLuggage';

export const PNR_TAB = 'PNR';
export const BOOKING_DETAILS_TAB = 'BOOKING-DETAILS';
export const BOOKINGS_PER_TRAIN_TAB = 'BOOKINGS-PER-TRAIN';
export const ON_HOLD_BOOKINGS_TAB = 'ON-HOLD-BOOKINGS';
export const BOOKINGS_WITH_SPECIAL_ASSISTANCE = 'BOOKINGS-WITH-SPECIAL-ASSISTANCE';
export const BOOKINGS_WITH_MINORS_TAB = 'BOOKINGS-WITH-MINORS';
export const BOOKINGS_WITH_LUGGAGE_TAB = 'BOOKINGS-WITH-LUGGAGE';

const BookingSearch = ({ intl, history, user, location }) => {
  const isStationUser = user?.isStation;

  const defaultTab = useDefaultTab(location);
  const [selectedTab, setSelectedTab] = useState(defaultTab);

  return (
    <Page maxWidth="70%" margin="auto">
      <Tabs
        selectedValue={selectedTab}
        onClick={setSelectedTab}
        tabs={[
          {
            value: PNR_TAB,
            label: <FormattedMessage id="searchBooking.pnr" />
          },
          {
            value: BOOKING_DETAILS_TAB,
            label: <FormattedMessage id="searchBooking.bookingDetails" />
          },
          ...(isUserEurostarAgent(user)
            ? [
                {
                  value: BOOKINGS_PER_TRAIN_TAB,
                  label: <FormattedMessage id="searchBooking.bookingPerTrain" />
                },
                {
                  value: BOOKINGS_WITH_SPECIAL_ASSISTANCE,
                  label: <FormattedMessage id="searchBooking.bookingsSpecialAssistance" />
                }
              ]
            : []),
          ...(!isStationUser
            ? [
                {
                  value: ON_HOLD_BOOKINGS_TAB,
                  label: <FormattedMessage id="searchBooking.onHold" />
                }
              ]
            : []),
          ...(isUserEurostarAgent(user)
            ? [
                {
                  value: BOOKINGS_WITH_MINORS_TAB,
                  label: <FormattedMessage id="searchBooking.bookingsWithMinors" />
                },
                {
                  value: BOOKINGS_WITH_LUGGAGE_TAB,
                  label: <FormattedMessage id="searchBooking.bookingsWithLuggage" />
                }
              ]
            : [])
        ]}
      />
      {selectedTab === PNR_TAB && (
        <SearchByPnrForm
          data-testid="search-by-pnr"
          onSubmitSearch={(values) => {
            const pnrNoSpace = values.reference.replace(/\s/g, '');
            history.push(`/bookings?reference=${pnrNoSpace}`);
          }}
        />
      )}
      {selectedTab === BOOKING_DETAILS_TAB && (
        <SearchByOtherDetailsForm
          data-testid="search-by-other-details"
          onSubmitSearch={(e) => {
            const { lastName, email, departureDate, travelEndDate, serviceName } = e;
            const formattedDepartureDate = moment(departureDate).format('YYYY-MM-DD');
            const formattedTravelEndDate = moment(travelEndDate).format('YYYY-MM-DD');
            const { push } = history;

            const params = new URLSearchParams();

            params.append('lastName', lastName);
            params.append('departureDate', formattedDepartureDate);
            params.append('travelEndDate', formattedTravelEndDate);
            params.append('email', email);

            if (serviceName) {
              params.append('serviceName', serviceName);
            }

            push(`/bookings?${params.toString()}`);
          }}
          intl={intl}
        />
      )}
      {selectedTab === BOOKINGS_PER_TRAIN_TAB && (
        <SearchPerTrainForm
          data-testid="search-per-train"
          onSubmitSearch={(e) => {
            const { departureDate, trainNumber, reference, coach, seat } = e;
            const formattedDate = moment(departureDate).format('YYYY-MM-DD');
            const { push } = history;

            const params = new URLSearchParams();

            params.append('departureDate', formattedDate);
            params.append('trainNumber', trainNumber);

            if (reference) {
              params.append('externalReference', reference);
            }

            if (coach) {
              params.append('coach', coach);
            }

            if (seat) {
              params.append('seat', seat);
            }

            push(`/bookings?${params.toString()}`);
          }}
        />
      )}
      {selectedTab === BOOKINGS_WITH_SPECIAL_ASSISTANCE && (
        <SearchBySpecialAssistance
          data-testid="search-by-special-assistance"
          onSubmitSearch={({ key, value }) => {
            history.push(`/bookings?detailKey=${key}&detailValue=${value}`);
          }}
        />
      )}
      {selectedTab === ON_HOLD_BOOKINGS_TAB && (
        <SearchByOnHoldPaymentMonth
          data-testid="search-by-onhold-payment-month"
          isNonEurostarUser={isTravelAgent(user)}
          onSubmitSearch={({ searchPaymentMonth, searchOfficeLocation }) => {
            history.push(`/bookings?expiry=${searchPaymentMonth}${!isTravelAgent(user) ? `&officeLocation=${searchOfficeLocation}` : ''}`);
          }}
          menuTab={true}
        />
      )}
      {selectedTab === BOOKINGS_WITH_MINORS_TAB && (
        <SearchByMinors
          data-testid="search-by-minors"
          defaultUserStation={user.defaultJourneyOrigin}
          onSubmitSearch={({ searchMinorsStation, searchMinorsPeriod }) => {
            history.push(`/bookings?period=${searchMinorsPeriod}&stationUic=${searchMinorsStation}`);
          }}
        />
      )}
      {selectedTab === BOOKINGS_WITH_LUGGAGE_TAB && (
        <SearchByLuggage
          data-testid="search-by-luggage"
          onSubmitSearch={({ station, date }) => {
            const formattedDate = moment(date).format('YYYY-MM-DD');
            history.push(`/bookings?travelDate=${formattedDate}&stationUIC=${station}`);
          }}
        />
      )}
    </Page>
  );
};

BookingSearch.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  user: PropTypes.shape({
    isStation: PropTypes.bool,
    defaultJourneyOrigin: PropTypes.string
  }),
  location: locationShape
};

export default withRouter(BookingSearch);
